import { Object3D } from "three";
import { BaseVisual } from "./BaseVisual";
import { Vec2 } from "../models/SyntheticMap";
import { Visual } from "../models/Visual";
import * as THREE from "three";
import { buildWall, copyToEuler, copyVec3 } from "./visual-utils";
import { CollisionBox } from "../models/Model";

export interface FloorVisualData extends Visual {
  wallHeight: number;
  outerWall: Vec2[];
  innerWalls: Vec2[][];
  doorIndexes: number[];
  scale: number;
}

export class FloorVisual extends BaseVisual implements FloorVisualData {
  wallHeight: number = 1;
  outerWall: Vec2[] = [
    { x: -1, y: -1 },
    { x: 1, y: -1 },
    { x: 1, y: 1 },
    { x: -1, y: 1 }
  ];
  innerWalls: Vec2[][] = [];
  doorIndexes: number[] = [];
  scale: number = 100;
  
  public getType(): string {
    return 'FloorVisual';
  }

  public container = new THREE.Group();
  protected async buildObj(): Promise<Object3D> {
    this.updateObj(); // Initialize the floor object
    return this.container;
  }

  public convertToLocal(point: THREE.Vector3): THREE.Vector3 {
    if (this.siteAnchorId) {
      const siteAnchor = this.wr.dataService.activeSite;
      if (siteAnchor) {
        return siteAnchor.convertToLocal(point);
      }
    }
    return this.obj.worldToLocal(point.clone());
  }


  public updateObj(): void {
    // render polygon shape based on points
    copyVec3(this.position, this.container.position);
    copyToEuler(this.rotation, this.container.rotation);
    this.container.clear();
    const shape = new THREE.Shape();
    if (this.outerWall.length > 0) {
      const firstPoint = this.outerWall[0];
      shape.moveTo(firstPoint.x, firstPoint.y);
      for (let i = 1; i < this.outerWall.length; i++) {
        const point = this.outerWall[i];
        shape.lineTo(point.x, point.y);
      }
      shape.lineTo(firstPoint.x, firstPoint.y); // Close the polygon
    }
    const geometry = new THREE.ShapeGeometry(shape);
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: 0x909090,
      roughness: 0.5,
      metalness: 0.1,
    });
    const wallMaterial = new THREE.MeshStandardMaterial({
      color: 0xa1a1a1,
      roughness: 0.5,
      metalness: 0.15,
      // transparent: true,
      // opacity: 0.5
    });
    const mesh = new THREE.Mesh(geometry, floorMaterial);
    mesh.name = "FloorVisualBottom";
    this.container.add(mesh);
    buildWall(this.container,
      this.outerWall.map(p => new THREE.Vector3(p.x, p.y, 0)),
      this.wallHeight,
      0.5,
      wallMaterial,
      0.1,
      this.doorIndexes
    );
    for (const innerWall of this.innerWalls) {
      buildWall(this.container,
        innerWall.map(p => new THREE.Vector3(p.x, p.y, 0)),
        this.wallHeight,
        0.5,
        wallMaterial,
        0.1,
        [innerWall.length - 1]
      );
    }
  }

  public getFrindlyName(): string {
    return this.id || "Floor Visual";
  }

  public createDataTemplate(): Visual {
    const data: FloorVisualData = {
      type: this.getType(),
      _id: this._id,
      id: this.id,
      position: this.position,
      rotation: this.rotation,
      orgId: this.orgId,
      siteAnchorId: this.siteAnchorId,
      location: this.location,
      wallHeight: 1,
      outerWall: [
        { x: -1, y: -1 },
        { x: 1, y: -1 },
        { x: 1, y: 1 },
        { x: -1, y: 1 }
      ],
      innerWalls: [],
      doorIndexes: [],
      scale: 100
    };
    return data;
  }

  protected getCollisionBoxes(): CollisionBox[] {
    const collisionBoxes: CollisionBox[] = [];
    this.obj.traverse((child) => {
      if (child instanceof THREE.Mesh && child.name.startsWith('wall_')) {
        const box: CollisionBox = {
          position: { x: 0, y: 0, z: 0 },
          rotation: { x: 0, y: 0, z: 0 },
          size: { x: 0, y: 0, z: 0 }
        };
        const boxGeometry = child.geometry as THREE.BoxGeometry;
        copyVec3(child.position, box.position);
        copyVec3(child.rotation, box.rotation);
        box.size.x = boxGeometry.parameters.width;
        box.size.y = boxGeometry.parameters.height;
        box.size.z = boxGeometry.parameters.depth;
        // console.log({ box });
        collisionBoxes.push(box);
      }
    });
    return collisionBoxes;
  }

}